import * as React from "react";
import { cn } from "../../lib/utils";
import { INPUT_STATES, inputVariants } from "./constants";
import type { InputProps, InputRootProps, InputIconProps } from "./types";
import { Slot } from "@radix-ui/react-slot";

const InputRoot = React.forwardRef<HTMLDivElement, InputRootProps>(
  (
    {
      className,
      children,
      state = INPUT_STATES.NORMAL,
      errorMessage,
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        <div ref={ref} className={cn("relative", className)} {...props}>
          {children}
        </div>
        {state === INPUT_STATES.ERROR && errorMessage && (
          <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
        )}
      </div>
    );
  }
);
InputRoot.displayName = "InputRoot";

const InputIcon = React.forwardRef<HTMLDivElement, InputIconProps>(
  ({ className, children }, ref) => {
    return (
      <Slot
        ref={ref}
        role="presentation"
        className={cn(
          "absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none size-5 [&~input]:pl-11",
          className
        )}
      >
        {children}
      </Slot>
    );
  }
);
InputIcon.displayName = "InputIcon";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, state = INPUT_STATES.NORMAL, inputSize, variant, bordered, ...props },
    ref
  ) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ state, size: inputSize, variant, bordered, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

const StandaloneInput = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      state = INPUT_STATES.NORMAL,
      inputSize,
      variant,
      bordered,
      errorMessage,
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        <input
          type={type}
          className={cn(inputVariants({ state, size: inputSize, variant, bordered, className }))}
          ref={ref}
          {...props}
        />
        {state === INPUT_STATES.ERROR &&
          errorMessage && (
            <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
          )}
      </div>
    );
  }
);
StandaloneInput.displayName = "Input";

export { InputRoot, InputIcon, Input };

export default StandaloneInput;
